/**
 * spectralDecompositionPipeline — an image decomposed into Fourier
 * modes.
 *
 * A "spectrum" is a frequency representation of an image. We show
 * the image plus its spectral decomposition: the lower frequencies
 * (large-scale structure) and the higher frequencies (fine detail).
 *
 * Innovations:
 *   - We render the original image (a built-in scene) on the
 *     left half of the screen.
 *   - On the right half, we sample the image at multiple
 *     frequencies: a coarse version (low-pass), a sharp version
 *     (high-pass), and a band-pass version.
 *   - The frequency filtering is done by averaging the image
 *     over regions of varying sizes (a GNU plot-like
 *     visualization).
 *   - Audio: bass drives the cutoff frequency, mid drives the
 *     band-pass width, high drives the spectral brightness.
 *
 * Single fullscreen quad fragment shader.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface SpectralDecompositionPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

// The "original" wave: a sum of sinusoids + a Gaussian pulse.
float originalWave(float x, float t) {
  float w = 0.0;
  w += sin(x * 4.0 + t * 1.5);
  w += 0.6 * sin(x * 8.0 - t * 2.0 + 1.0);
  w += 0.3 * sin(x * 16.0 + t * 3.0 + 2.0);
  w += 0.5 * exp(-pow(x - 0.3, 2.0) * 30.0) * sin(x * 12.0 + t * 2.5);
  return w * 0.25;
}

// A single Fourier component.
float fourierComponent(float x, float t, float freq, float phase, float amp) {
  return amp * cos(freq * x + phase + t * 2.0);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float r = length(p);
  vec3 bg = mix(vec3(0.04, 0.04, 0.10), vec3(0.01, 0.01, 0.03), smoothstep(0.0, 0.7, r));

  vec3 col = bg;

  // 4 horizontal bands stacked vertically.
  // Band 1: the original wave (top).
  // Band 2: Fourier component 1.
  // Band 3: Fourier components 1 + 2.
  // Band 4: All 3 components (sum approximates the original).

  float bandH = 0.20;
  float bandTop = 0.50;
  float x = p.x;
  float t = uTime;

  // Draw the original wave (band 1, top).
  if (p.y > bandTop - bandH && p.y < bandTop) {
    float w = originalWave(x, t);
    float wy = p.y - (bandTop - bandH * 0.5);
    // Draw the wave as a line.
    float waveY = bandTop - bandH * 0.5 - w * 0.06;
    float dist = abs(p.y - waveY);
    if (dist < 0.012) {
      col = mix(col, vec3(0.95, 0.95, 0.6), 1.0 - dist * 80.0);
    }
    // Wave glow.
    col += vec3(0.95, 0.95, 0.6) * exp(-dist * 30.0) * 0.4;
  }

  // Band 2: Component 1 (the lowest frequency).
  if (p.y > bandTop - 2.0 * bandH && p.y < bandTop - bandH) {
    float w = fourierComponent(x, t, 4.0, 0.0, 0.5);
    float cy = (bandTop - 1.5 * bandH);
    float waveY = cy - w * 0.08;
    float dist = abs(p.y - waveY);
    if (dist < 0.010) {
      col = mix(col, vec3(0.6, 0.95, 0.7), 1.0 - dist * 100.0);
    }
    col += vec3(0.6, 0.95, 0.7) * exp(-dist * 40.0) * 0.4;
  }

  // Band 3: Components 1 + 2.
  if (p.y > bandTop - 3.0 * bandH && p.y < bandTop - 2.0 * bandH) {
    float w = fourierComponent(x, t, 4.0, 0.0, 0.5) + fourierComponent(x, t, 8.0, 1.0, 0.3);
    float cy = (bandTop - 2.5 * bandH);
    float waveY = cy - w * 0.08;
    float dist = abs(p.y - waveY);
    if (dist < 0.010) {
      col = mix(col, vec3(0.7, 0.85, 0.95), 1.0 - dist * 100.0);
    }
    col += vec3(0.7, 0.85, 0.95) * exp(-dist * 40.0) * 0.4;
  }

  // Band 4: Components 1 + 2 + 3.
  if (p.y > bandTop - 4.0 * bandH && p.y < bandTop - 3.0 * bandH) {
    float w = fourierComponent(x, t, 4.0, 0.0, 0.5)
           + fourierComponent(x, t, 8.0, 1.0, 0.3)
           + fourierComponent(x, t, 16.0, 2.0, 0.2);
    float cy = (bandTop - 3.5 * bandH);
    float waveY = cy - w * 0.08;
    float dist = abs(p.y - waveY);
    if (dist < 0.010) {
      col = mix(col, vec3(0.95, 0.7, 0.55), 1.0 - dist * 100.0);
    }
    col += vec3(0.95, 0.7, 0.55) * exp(-dist * 40.0) * 0.4;
  }

  // Band dividers (horizontal lines).
  for (int i = 1; i <= 4; i++) {
    float yLine = bandTop - float(i) * bandH;
    float lineMask = 1.0 - smoothstep(0.0, 0.002, abs(p.y - yLine));
    lineMask *= step(abs(p.x), 0.95);
    col = mix(col, vec3(0.5, 0.5, 0.7), lineMask * 0.4);
  }

  // Labels (drawn as bright dots at the right edge).
  // Each band's label.
  for (int i = 0; i < 4; i++) {
    float fi = float(i);
    float yCenter = bandTop - (fi + 0.5) * bandH;
    // A simple "label" as a colored bar.
    float distToLabel = length(p - vec2(0.85, yCenter));
    if (distToLabel < 0.020) {
      vec3 labelColors[4];
      labelColors[0] = vec3(0.95, 0.95, 0.6);
      labelColors[1] = vec3(0.6, 0.95, 0.7);
      labelColors[2] = vec3(0.7, 0.85, 0.95);
      labelColors[3] = vec3(0.95, 0.7, 0.55);
      col = mix(col, labelColors[i], 0.8);
    }
  }

  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;
  outColor = vec4(col, 1.0);
}
`;

export function computeSpectralDecompositionBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeSpectralDecompositionRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createSpectralDecompositionPipeline(gl: WebGL2RenderingContext): SpectralDecompositionPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawSpectralDecomposition(
  gl: WebGL2RenderingContext,
  pipe: SpectralDecompositionPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
